import { createAsyncThunk } from "@reduxjs/toolkit"
import moment from "moment-timezone"
import * as PapaParse from "papaparse"

import { AmplitudeEvent } from "@common-shared/config/amplitude.config.ts"
import {
    PolicyAssignmentPriority,
    PolicyAssignmentStatus,
    PolicyReassignmentReason,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import { IPagination } from "@common-shared/types/general.types.ts"
import { ICoverageHistoryListSort } from "@common-shared/types/internalApis/coverageHistory.types.ts"
import {
    IPoliciesDateRangeFilterData,
    IPoliciesListFiltersData,
} from "@common-shared/types/policies.types.ts"
import type { IPractice } from "@common-shared/types/responseData/commonResponseData.types.ts"
import {
    IOneVerifyAssignmentsProductivityExportRow,
    IOneVerifyCoverageCheckHistory,
    IOneVerifyCoverageResult,
} from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { ToastrHelper } from "@front-shared/helpers/toastr.helper.tsx"

import { CHECKER_CONFIG } from "../../config/checker.config.ts"
import { GENERAL_CONFIG } from "../../config/general.config.ts"
import { POLICIES_CONFIG } from "../../config/policies.config.tsx"
import { AmplitudeHelper } from "../../helpers/amplitude.helper.tsx"
import { CsvHelper } from "../../helpers/csv.helper.tsx"
import { TaskAssignmentsService } from "../../services/taskAssignments.service.ts"
import { ICoverageHistoryListResult } from "../../types/data.types.ts"
import { IThunkActionWithPracticeData } from "../../types/general.types.ts"
import { IAssignmentIdentification } from "../../types/objects.types.ts"
import {
    policiesSetGettingFullListProgress,
    policiesSetTaskAssignmentsList,
} from "../slices/policies.slice.ts"
import {
    runtimeStartLoading,
    runtimeStopLoading,
} from "../slices/runtime.slice.ts"
import type { RootState } from "../store"
import { userDetailsGetPracticeUsageQuota } from "./userDetails.thunks.ts"

/**
 * Get list of task assignments
 */
export const taskAssignmentsGetList = createAsyncThunk<
    ICoverageHistoryListResult,
    IThunkActionWithPracticeData<{
        pagination: IPagination
        filters?: IPoliciesListFiltersData
        sort?: ICoverageHistoryListSort
    }>
>("taskAssignmentsGetListPrefix", async (action, { dispatch, signal }) => {
    dispatch(runtimeStartLoading("taskAssignmentsGetListLoading"))

    const defaultResult: ICoverageHistoryListResult = {
        items: [],
        newPagination: action.payload.pagination,
        isError: false,
    }

    // reset items
    dispatch(policiesSetTaskAssignmentsList(defaultResult))

    try {
        const result = await TaskAssignmentsService.getTaskAssignments(
            action.practice,
            action.payload.pagination,
            action.payload.filters,
            action.payload.sort,
            signal,
        )

        if (result) {
            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }

        return result || defaultResult
    } catch (e) {
        action.onError?.()

        return defaultResult
    } finally {
        // Only stop loading if it wasn't aborted by other trigger
        if (!signal.aborted) {
            dispatch(runtimeStopLoading("taskAssignmentsGetListLoading"))
        }
    }
})

/**
 * Update task assignment status
 */
export const taskAssignmentsUpdateStatus = createAsyncThunk<
    IOneVerifyCoverageCheckHistory | null,
    IThunkActionWithPracticeData<{
        assignment: IAssignmentIdentification
        status: PolicyAssignmentStatus
    }>
>("taskAssignmentsUpdateStatusPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("taskAssignmentsUpdateStatusLoading"))

    try {
        const result = await TaskAssignmentsService.updateTaskAssignmentStatus(
            action.practice,
            action.payload.assignment,
            action.payload.status,
        )

        if (result) {
            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }

        AmplitudeHelper.logEvent(AmplitudeEvent.POLICY_CHANGE_STATUS_SINGLE, {
            practiceId: action.practice.id,
            practiceName: action.practice.name,
            status: action.payload.status,
        })

        return result || null
    } catch (e) {
        action.onError?.()

        return null
    } finally {
        dispatch(runtimeStopLoading("taskAssignmentsUpdateStatusLoading"))

        dispatch(
            userDetailsGetPracticeUsageQuota({
                payload: action.practice,
            }),
        )
    }
})

/**
 * Bulk assign task assignments to a user
 * Handles partial success - proceeds with assignments that don't have collisions
 */
export const taskAssignmentsBulkAssign = createAsyncThunk<
    IOneVerifyCoverageResult | null,
    IThunkActionWithPracticeData<{
        nirvanaRequestIds: string[]
        assigneeId: number
        priority?: PolicyAssignmentPriority
    }>
>(
    "taskAssignmentsBulkAssignPrefix",
    async (action, { dispatch }): Promise<any> => {
        dispatch(runtimeStartLoading("taskAssignmentsBulkAssignLoading"))

        let processedCount = 0

        try {
            let results: (IOneVerifyCoverageCheckHistory | undefined)[] = []

            let successCount = 0

            let failureCount = 0

            while (processedCount < action.payload.nirvanaRequestIds.length) {
                const promises: Promise<
                    IOneVerifyCoverageCheckHistory | undefined
                >[] = []

                // Generate processing promises with our "pagination' based on batch size
                for (
                    let i = processedCount;
                    i < processedCount + CHECKER_CONFIG.processBatchSize;
                    i++
                ) {
                    if (!action.payload.nirvanaRequestIds[i]) {
                        break
                    }

                    promises.push(
                        TaskAssignmentsService.createTaskAssignment(
                            action.practice,
                            action.payload.nirvanaRequestIds[i],
                            action.payload.assigneeId,
                            action.payload.priority,
                        ).catch(() => {
                            // Return undefined for failed assignments so we can track them
                            failureCount++

                            return undefined
                        }),
                    )
                }

                processedCount += CHECKER_CONFIG.processBatchSize

                // Use Promise.allSettled to continue processing even if some fail
                const batchResults = await Promise.allSettled(promises)

                // Extract successful results
                const successfulResults = batchResults
                    .filter(
                        result =>
                            result.status === "fulfilled" &&
                            result.value !== undefined,
                    )
                    .map(
                        result =>
                            (
                                result as PromiseFulfilledResult<IOneVerifyCoverageCheckHistory>
                            ).value,
                    )

                results = [...results, ...successfulResults]

                successCount += successfulResults.length
            }

            AmplitudeHelper.logEvent(
                action.payload.nirvanaRequestIds.length > 1
                    ? AmplitudeEvent.POLICY_ASSIGN_BULK
                    : AmplitudeEvent.POLICY_ASSIGN_SINGLE,
                {
                    practiceId: action.practice.id,
                    practiceName: action.practice.name,
                    successCount,
                    failureCount,
                },
            )

            // Call onSuccess even if there were some failures (partial success)
            // The results array will only contain successful assignments
            action.onSuccess?.(results)
        } catch (e) {
            // Only call onError for unexpected errors, not for individual assignment failures
            action.onError?.()
        } finally {
            dispatch(runtimeStopLoading("taskAssignmentsBulkAssignLoading"))
        }
    },
)

/**
 * Bulk update status of task assignments
 */
export const taskAssignmentsBulkUpdateStatus = createAsyncThunk<
    IOneVerifyCoverageResult | null,
    IThunkActionWithPracticeData<{
        practice: IPractice
        assignments: IAssignmentIdentification[]
        status: PolicyAssignmentStatus
    }>
>(
    "taskAssignmentsBulkUpdateStatusPrefix",
    async (action, { dispatch }): Promise<any> => {
        dispatch(runtimeStartLoading("taskAssignmentsBulkUpdateStatusLoading"))

        let processedCount = 0

        try {
            let results: (IOneVerifyCoverageCheckHistory | undefined)[] = []

            while (processedCount < action.payload.assignments.length) {
                const promises: Promise<
                    IOneVerifyCoverageCheckHistory | undefined
                >[] = []

                // Generate processing promises with our "pagination' based on batch size
                for (
                    let i = processedCount;
                    i < processedCount + CHECKER_CONFIG.processBatchSize;
                    i++
                ) {
                    if (!action.payload.assignments[i]) {
                        break
                    }

                    promises.push(
                        TaskAssignmentsService.updateTaskAssignmentStatus(
                            action.practice,
                            action.payload.assignments[i],
                            action.payload.status,
                        ),
                    )
                }

                processedCount += CHECKER_CONFIG.processBatchSize

                const batchResults = await Promise.all(promises)

                results = [...results, ...batchResults]
            }

            AmplitudeHelper.logEvent(
                action.payload.assignments.length > 1
                    ? AmplitudeEvent.POLICY_CHANGE_STATUS_BULK
                    : AmplitudeEvent.POLICY_CHANGE_STATUS_SINGLE,
                {
                    practiceId: action.practice.id,
                    practiceName: action.practice.name,
                    status: action.payload.status,
                },
            )

            action.onSuccess?.(results)
        } catch (e) {
            action.onError?.()
        } finally {
            dispatch(
                runtimeStopLoading("taskAssignmentsBulkUpdateStatusLoading"),
            )
        }
    },
)

/**
 * Bulk update priority of task assignments
 */
export const taskAssignmentsBulkUpdatePriority = createAsyncThunk<
    IOneVerifyCoverageResult | null,
    IThunkActionWithPracticeData<{
        practice: IPractice
        assignments: IAssignmentIdentification[]
        priority: PolicyAssignmentPriority
    }>
>(
    "taskAssignmentsBulkUpdatePriorityPrefix",
    async (action, { dispatch }): Promise<any> => {
        dispatch(
            runtimeStartLoading("taskAssignmentsBulkUpdatePriorityLoading"),
        )

        let processedCount = 0

        try {
            let results: (IOneVerifyCoverageCheckHistory | undefined)[] = []

            while (processedCount < action.payload.assignments.length) {
                const promises: Promise<
                    IOneVerifyCoverageCheckHistory | undefined
                >[] = []

                // Generate processing promises with our "pagination' based on batch size
                for (
                    let i = processedCount;
                    i < processedCount + CHECKER_CONFIG.processBatchSize;
                    i++
                ) {
                    if (!action.payload.assignments[i]) {
                        break
                    }

                    promises.push(
                        TaskAssignmentsService.updateTaskAssignmentPriority(
                            action.practice,
                            action.payload.assignments[i],
                            action.payload.priority,
                        ),
                    )
                }

                processedCount += CHECKER_CONFIG.processBatchSize

                const batchResults = await Promise.all(promises)

                results = [...results, ...batchResults]
            }

            AmplitudeHelper.logEvent(
                action.payload.assignments.length > 1
                    ? AmplitudeEvent.POLICY_CHANGE_PRIORITY_BULK
                    : AmplitudeEvent.POLICY_CHANGE_PRIORITY_SINGLE,
                {
                    practiceId: action.practice.id,
                    practiceName: action.practice.name,
                    priority: action.payload.priority,
                },
            )

            action.onSuccess?.(results)
        } catch (e) {
            action.onError?.()
        } finally {
            dispatch(
                runtimeStopLoading("taskAssignmentsBulkUpdatePriorityLoading"),
            )
        }
    },
)

/**
 * Bulk reassign task assignments to a user
 */
export const taskAssignmentsBulkReassign = createAsyncThunk<
    IOneVerifyCoverageResult | null,
    IThunkActionWithPracticeData<{
        assignments: IAssignmentIdentification[]
        assigneeId?: number // no assignee = unassign
        reassignmentReason?: PolicyReassignmentReason
    }>
>(
    "taskAssignmentsBulkReassignPrefix",
    async (action, { dispatch }): Promise<any> => {
        dispatch(runtimeStartLoading("taskAssignmentsBulkReassignLoading"))

        let processedCount = 0

        try {
            let results: (IOneVerifyCoverageCheckHistory | undefined)[] = []

            while (processedCount < action.payload.assignments.length) {
                const promises: Promise<
                    IOneVerifyCoverageCheckHistory | undefined
                >[] = []

                // Generate processing promises with our "pagination' based on batch size
                for (
                    let i = processedCount;
                    i < processedCount + CHECKER_CONFIG.processBatchSize;
                    i++
                ) {
                    if (!action.payload.assignments[i]) {
                        break
                    }

                    promises.push(
                        TaskAssignmentsService.reassignTaskAssignment(
                            action.practice,
                            action.payload.assignments[i],
                            action.payload.reassignmentReason,
                            action.payload.assigneeId,
                        ),
                    )
                }

                processedCount += CHECKER_CONFIG.processBatchSize

                const batchResults = await Promise.all(promises)

                results = [...results, ...batchResults]
            }

            AmplitudeHelper.logEvent(
                action.payload.assignments.length > 1
                    ? AmplitudeEvent.POLICY_REASSIGN_BULK
                    : AmplitudeEvent.POLICY_REASSIGN_SINGLE,
                {
                    practiceId: action.practice.id,
                    practiceName: action.practice.name,
                    reassignmentReason: action.payload.reassignmentReason,
                },
            )

            action.onSuccess?.(results)
        } catch (e) {
            action.onError?.()
        } finally {
            dispatch(runtimeStopLoading("taskAssignmentsBulkReassignLoading"))
        }
    },
)

/**
 * Export tasks assignments productivity report
 */
export const taskAssignmentsExportProductivity = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        dateAssigned: IPoliciesDateRangeFilterData
    }>
>(
    "taskAssignmentsExportProductivityPrefix",
    async (action, { dispatch, getState }) => {
        dispatch(policiesSetGettingFullListProgress(0))
        dispatch(
            runtimeStartLoading("taskAssignmentsExportProductivityLoading"),
        )

        try {
            const { dateAssigned } = action.payload

            const countResponse =
                await TaskAssignmentsService.getTaskAssignmentsProductivityExportCount(
                    action.practice,
                    {
                        dateAssigned,
                    },
                )

            if (!countResponse) {
                throw new Error("Failed to fetch export total count")
            }

            const totalItems = countResponse.total || 0

            if (!totalItems) {
                ToastrHelper.warning(
                    "No completed assignments found for the selected date range",
                )

                return
            }

            const items: IOneVerifyAssignmentsProductivityExportRow[] = []
            let moreAvailable = true
            let cursor: string | undefined = undefined
            let safetyCounter = 0 // So it's not an infinite loop in case the API malfunctions and always returns moreAvailable=true

            while (moreAvailable && safetyCounter < 1000) {
                const chunk =
                    await TaskAssignmentsService.getTaskAssignmentsProductivityExport(
                        action.practice,
                        {
                            dateAssigned,
                            count: POLICIES_CONFIG.assignmentsProductivityExport
                                .defaultChunkSize,
                            cursor,
                        },
                    )

                if (!chunk) {
                    throw new Error("Failed to fetch export chunk")
                }

                items.push(...chunk.items)

                moreAvailable = chunk.moreAvailable
                cursor = chunk.nextCursor || undefined
                safetyCounter += 1

                dispatch(
                    policiesSetGettingFullListProgress(
                        Math.max(
                            (getState() as RootState).policies
                                .policiesGettingFullListProgress,
                            Math.min(
                                99,
                                Math.floor((items.length / totalItems) * 100),
                            ),
                        ),
                    ),
                )
            }

            if (!items.length) {
                ToastrHelper.warning(
                    "No completed assignments found for the selected date range",
                )

                return
            }

            const csvData: string[][] = [
                // headers
                POLICIES_CONFIG.taskAssignmentsProductivityExportColumns.map(
                    item => item.header,
                ),
            ]

            for (const item of items) {
                const row: string[] = []

                for (const column of POLICIES_CONFIG.taskAssignmentsProductivityExportColumns) {
                    let value = item[column.valuePath] ?? ""

                    if (value && column.isDateTime) {
                        value = moment(value).format(
                            GENERAL_CONFIG.defaultMomentDateTimeFormat,
                        )
                    }

                    row.push(value?.toString() ?? "")
                }

                csvData.push(row)
            }

            CsvHelper.createAndDownloadCsvDocument(
                PapaParse.unparse(csvData),
                `workflow-management-productivity-${dateAssigned.dateFrom}-to-${dateAssigned.dateTo}`,
                false,
            )
        } catch (e) {
            action.onError?.()

            ToastrHelper.error(
                "Failed to export workflow management productivity CSV",
            )
        } finally {
            dispatch(policiesSetGettingFullListProgress(100))

            dispatch(
                runtimeStopLoading("taskAssignmentsExportProductivityLoading"),
            )
        }
    },
)
